import React, {useState, useEffect, useRef} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {Search, Plus, ArrowLeft, FlaskConical, ClipboardList} from 'lucide-react';
import {ProviderOrderTestsService} from '@/modules/order-tests/services/providerOrderTestsService';
import PatientEditModal, {PatientEditButton} from '@/modules/provider-console/components/PatientEditModal';
import Button from '@/shared/components/Button';
import type {OrderTestsPatientSearchItem} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';

const AVATAR_COLORS = [
    'bg-mld-warning/10 text-mld-warning',
    'bg-sky-100 text-sky-700',
    'bg-violet-100 text-violet-700',
    'bg-mld-danger/10 text-mld-danger',
    'bg-mld-success/10 text-mld-success',
    'bg-orange-100 text-orange-700',
];

function getInitials(first: string, last: string): string {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getAvatarColor(first: string, last: string): string {
    const hash = (first.charCodeAt(0) || 0) + (last.charCodeAt(0) || 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface PatientSearchProps {
    onCreateOrder: (patient: OrderTestsPatientSearchItem) => void;
    onSaveToQueue: (patient: OrderTestsPatientSearchItem) => void;
    onCreatePatient: (patient: {
        firstName: string;
        lastName: string;
        email: string;
        birthDate: string;
        gender: string;
    }) => void;
    isAdding?: boolean;
    isCreating?: boolean;
}

const PatientSearch: React.FC<PatientSearchProps> = ({
    onCreateOrder,
    onSaveToQueue,
    onCreatePatient,
    isAdding,
    isCreating,
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<OrderTestsPatientSearchItem | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isPatientEditOpen, setIsPatientEditOpen] = useState(false);
    const queryClient = useQueryClient();
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Create form state
    const [newFirstName, setNewFirstName] = useState('');
    const [newLastName, setNewLastName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newBirthDate, setNewBirthDate] = useState('');
    const [newGender, setNewGender] = useState('');

    // Debounce search input (300ms)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Show dropdown when debounced search changes from user typing (not from selecting a patient)
    const skipNextDropdownRef = useRef(false);
    useEffect(() => {
        if (skipNextDropdownRef.current) {
            skipNextDropdownRef.current = false;
            return;
        }
        if (debouncedSearch.length >= 2) {
            setShowDropdown(true);
            setSelectedPatient(null);
        } else {
            setShowDropdown(false);
        }
    }, [debouncedSearch]);

    // Close dropdown on click outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const patientsQuery = useQuery({
        queryKey: ['order-tests-patient-search', debouncedSearch],
        queryFn: () => ProviderOrderTestsService.searchPatients(debouncedSearch),
        enabled: debouncedSearch.length >= 2,
    });

    const patients = patientsQuery.data?.patients ?? [];

    const handleSelectPatient = (patient: OrderTestsPatientSearchItem) => {
        skipNextDropdownRef.current = true;
        setSelectedPatient(patient);
        setShowDropdown(false);
        setSearch(`${patient.firstName} ${patient.lastName}`);
    };

    const handleClearSelection = () => {
        setSelectedPatient(null);
        setSearch('');
        setDebouncedSearch('');
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleOpenCreateForm = () => {
        const parts = search.trim().split(/\s+/);
        setNewFirstName(parts[0] ?? '');
        setNewLastName(parts.slice(1).join(' '));
        setShowDropdown(false);
        setShowCreateForm(true);
    };

    const handleCreate = () => {
        if (!newFirstName.trim() || !newLastName.trim() || !newEmail.trim()) return;
        onCreatePatient({
            firstName: newFirstName.trim(),
            lastName: newLastName.trim(),
            email: newEmail.trim(),
            birthDate: newBirthDate,
            gender: newGender,
        });
    };

    const handleBackToSearch = () => {
        setShowCreateForm(false);
        setNewFirstName('');
        setNewLastName('');
        setNewEmail('');
        setNewBirthDate('');
        setNewGender('');
    };

    const canCreate = newFirstName.trim() && newLastName.trim() && newEmail.trim() && newBirthDate && newGender;

    /* ── Create patient form ── */
    if (showCreateForm) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={handleBackToSearch}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div>
                        <h3 className="text-base font-bold text-gray-900">Create New Patient</h3>
                        <p className="text-xs text-gray-500">Enter patient details below</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="newFirstName" className="mb-1 block text-xs font-medium text-gray-700">
                            First Name *
                        </label>
                        <input
                            id="newFirstName"
                            type="text"
                            value={newFirstName}
                            onChange={(e) => setNewFirstName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label htmlFor="newLastName" className="mb-1 block text-xs font-medium text-gray-700">
                            Last Name *
                        </label>
                        <input
                            id="newLastName"
                            type="text"
                            value={newLastName}
                            onChange={(e) => setNewLastName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="newEmail" className="mb-1 block text-xs font-medium text-gray-700">
                        Email *
                    </label>
                    <input
                        id="newEmail"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="newBirthDate" className="mb-1 block text-xs font-medium text-gray-700">
                            Date of Birth *
                        </label>
                        <input
                            id="newBirthDate"
                            type="date"
                            value={newBirthDate}
                            onChange={(e) => setNewBirthDate(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label htmlFor="newGender" className="mb-1 block text-xs font-medium text-gray-700">
                            Gender *
                        </label>
                        <select
                            id="newGender"
                            value={newGender}
                            onChange={(e) => setNewGender(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                            <option value="">Select...</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end pt-1">
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={!canCreate || isCreating}
                        className="rounded-lg bg-mld-primary px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-mld-primary/90 disabled:opacity-50 transition-colors">
                        {isCreating ? 'Creating...' : 'Create & Add to Queue'}
                    </button>
                </div>
            </div>
        );
    }

    /* ── Search + dropdown ── */
    return (
        <div ref={containerRef}>
            {/* Search input */}
            <label htmlFor="patientSearch" className="mb-1.5 block text-sm font-medium text-gray-700">
                Patient <span className="text-mld-danger/60">*</span>
            </label>
            <div className="relative">
                <input
                    ref={inputRef}
                    id="patientSearch"
                    type="text"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setSelectedPatient(null);
                    }}
                    onFocus={() => {
                        if (debouncedSearch.length >= 2 && !selectedPatient) setShowDropdown(true);
                    }}
                    placeholder="Search by name, email, or date of birth..."
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                />
                <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                {/* Dropdown */}
                {showDropdown && (
                    <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg">
                        <div className="max-h-64 overflow-y-auto">
                            {patientsQuery.isLoading ? (
                                <p className="px-4 py-4 text-sm text-gray-400">Searching...</p>
                            ) : patients.length === 0 ? (
                                <p className="px-4 py-4 text-sm text-gray-400">No patients found</p>
                            ) : (
                                patients.map((patient, idx) => {
                                    const initials = getInitials(patient.firstName, patient.lastName);
                                    const colorClass = getAvatarColor(patient.firstName, patient.lastName);
                                    return (
                                        <button
                                            key={patient.dashboardUserId ?? `elis-${idx}`}
                                            type="button"
                                            onClick={() => handleSelectPatient(patient)}
                                            className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-mld-warning/5 transition-colors">
                                            <div
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${colorClass}`}>
                                                {initials}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-gray-900">
                                                    {patient.firstName} {patient.lastName}
                                                </p>
                                                <p className="truncate text-xs text-gray-500">
                                                    {patient.email ? `${patient.email} · ` : ''}
                                                    DOB: {formatBirthdate(patient.birthDate) || 'N/A'}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Create new patient link */}
                        {!patientsQuery.isLoading && (
                            <div className="border-t border-gray-100 px-4 py-2.5">
                                <button
                                    type="button"
                                    onClick={handleOpenCreateForm}
                                    className="flex items-center gap-2 text-sm font-medium text-mld-primary hover:text-mld-primary/80 transition-colors">
                                    <Plus className="h-4 w-4" />
                                    Create new patient...
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Selected patient — action buttons */}
            {selectedPatient && (
                <div className="mt-4 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${getAvatarColor(selectedPatient.firstName, selectedPatient.lastName)}`}>
                        {getInitials(selectedPatient.firstName, selectedPatient.lastName)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                            {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                            {selectedPatient.email ? `${selectedPatient.email} · ` : ''}
                            DOB: {formatBirthdate(selectedPatient.birthDate) || 'N/A'}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            disabled={isAdding}
                            onClick={() => onCreateOrder(selectedPatient)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-mld-primary px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-mld-primary/90 disabled:opacity-50 transition-colors">
                            <FlaskConical className="h-3.5 w-3.5" />
                            Create Lab Order
                        </button>
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={isAdding}
                            onClick={() => onSaveToQueue(selectedPatient)}
                            iconLeft={<ClipboardList className="h-3.5 w-3.5" />}>
                            Save to Queue
                        </Button>
                        {/* Edit is only available for patients that already exist
                         * in the dashboard DB (have a dashboardUserId). ELIS-only
                         * results don't have a row to update yet. */}
                        <PatientEditButton
                            disabled={!selectedPatient.dashboardUserId}
                            onClick={() => setIsPatientEditOpen(true)}
                            title={
                                selectedPatient.dashboardUserId
                                    ? 'Edit patient demographics'
                                    : 'Patient must be added to the queue before they can be edited'
                            }
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleClearSelection}
                        className="ml-1 text-xs text-gray-400 hover:text-gray-600">
                        Clear
                    </button>
                </div>
            )}
            <PatientEditModal
                isOpen={isPatientEditOpen}
                userId={selectedPatient?.dashboardUserId ?? null}
                onClose={() => setIsPatientEditOpen(false)}
                onSaved={(profile) => {
                    // Patch local selection so the search-result card reflects
                    // edits without needing a full re-search.
                    setSelectedPatient((current) =>
                        current && current.dashboardUserId === profile.id
                            ? {
                                  ...current,
                                  firstName: profile.firstName,
                                  lastName: profile.lastName,
                                  birthDate: profile.birthday ?? current.birthDate,
                                  gender: profile.gender ?? current.gender,
                              }
                            : current
                    );
                    queryClient.invalidateQueries({queryKey: ['order-tests-patient-search']});
                }}
            />
        </div>
    );
};

export default PatientSearch;
